import { useState, useEffect }  from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Specification } from 'Ui/Layer/Specification'
import { ihm_viewer__dictionary, locale } from 'Application/i18n/i18nViewer'
import { ModelSelector } from 'Ui/Components/ModelSelector'
import { CompactPlayer } from 'Ui/Components/CompactPlayer'
import { MobileMovementsSection } from 'Ui/Components/movements/MovementsMobile'
import { MobileIprAttSection } from 'Ui/Components/iprAtt/IprAttMobile'
import { MobileMeasuresSection } from 'Ui/Components/measures/MeasuresMobile'
import { hasMovementHistory } from 'Domain/Scene/Builder/VersionHistoryService'

import { applyMandiAdjust } from 'Domain/Scene/Handler/MandiAdjustHandler'
import 'Application/Templates/Css/Layer/SpecsMobile.css'


const TablesIcon = () => (
    <img src="/tableau-icon.svg" alt="" width="24" height="24" style={{ transform: 'scale(1.8)' }} />
);

const ChatIcon = () => (
    <img src="/chat-mobile-icon.svg" alt="" width="24" height="24" style={{ transform: 'scale(1.8)' }} />
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);


/*****************************************************************
 * Footer
 * ***************************************************************/

export const Footer = ({ isPortrait }) => {
    const dispatch = useDispatch();
    const [specs, setSpecs] = useState({});
    const [isModelSelectorVisible, setIsModelSelectorVisible] = useState(false);
    const [tablesOpen, setTablesOpen] = useState(false);

    const { AnimationReducer: animationState, ViewportReducer: viewportState, UserReducer: userState, LayoutReducer: layoutState } = useSelector(s => s);
    const footerVisibility = viewportState.footerVisibility;
    const metas = animationState.metas;
    const activeSpecsEntries = Object.entries(specs).filter(([_, v]) => v.exists);
    const activeSpecNames = activeSpecsEntries.map(([name]) => name);
    const dataTableAccessDenied = userState.deniedServices.includes('dataTable');
    const viewerType = viewportState.viewerType;

    const toggleModelSelectorVisibility = () => setIsModelSelectorVisible(prev => !prev);


    const updateFooterVisibility = (payload) => {
        if (payload) {
            dispatch({ type: 'CLOSE_LIVE', payload: null })
        }
        dispatch({ type: 'FOOTER_VISIBLE', payload })
    }

    const openModelSection = () => toggleModelSelectorVisibility();

    const openGenericSection = (section) => updateFooterVisibility(section === footerVisibility ? false : section);

    const openSection = (section) => {
        if (section === 'mod') return openModelSection();
        return openGenericSection(section);
    };

    useEffect(() => {
        if (!metas.treatment) {
            setSpecs({});
            return;
        }

        metas.treatment.att = metas.treatment['attachment'];
        metas.treatment.mea = metas.treatment['measure'];
        
        const modifications = viewportState.modifications;
        const selectedVersion = viewportState.selectedVersion;
        const hasHistory = hasMovementHistory(modifications, selectedVersion);

        setSpecs({
            mod: { exists: viewerType === 'simple', TRANS: 'SPE_MODEL' },
            mvt: { exists: metas.treatment['mvt'] !== 'none', TRANS: 'SPE_MOVEMENTS' },
            att: { exists: (metas.treatment['attachment'] !== 'none' || metas.treatment['ipr'] !== 'none'), TRANS: 'SPE_IPR_ATT' },
            mea: { exists: metas.treatment['measure'] !== 'none', TRANS: 'SPE_MEASURES' },
            hist: { exists: hasHistory, TRANS: 'SPE_MOVEMENT_HISTORY' },
            note: { exists: selectedVersion !== -1, TRANS: 'SPE_NOTE' }
        });
    }, [metas, viewerType, viewportState.selectedVersion, viewportState.mode, viewportState.modifications]);

    const mandiAdjustActive = viewportState.mandiAdjustActive;

    useEffect(() => {
        applyMandiAdjust(viewportState.mandiAdjust, mandiAdjustActive);
    }, [mandiAdjustActive, viewportState.mandiAdjust]);

    const shouldShowLiveButtons = userState.connected && !userState.expired && userState.hasLiveAccess;
    const isEditMode = viewportState.mode === 'edition';
    const shouldShowBottomLogo = !userState.connected;
    const getAnonymous = (name = '') => name.split(' ').map(n => n[0] + 'xxxx').join('');

    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    useEffect(() => {
        if (tablesOpen) {
            setCurrentSlideIndex(0);
        }
    }, [tablesOpen]);

    const handleTablesScroll = (e) => {
        if (!e.target || e.target.clientWidth === 0) return;
        const index = Math.round(e.target.scrollLeft / e.target.clientWidth);
        if (index !== currentSlideIndex) {
            setCurrentSlideIndex(index);
        }
    };

    /* ================================================================
     * Portrait mobile layout
     * ================================================================ */
    if (isPortrait) {
        const isConnected = userState.connected;
        const hasCaseData = !!metas?.caseData;
        const patientName = hasCaseData ? metas.caseData.patient.patientName : '';
        const activeSpecs = Object.entries(specs)
            .filter(([key, v]) => key !== 'mod' && v.exists)
            .map(([key]) => key);

        const orderedSlideKeys = ['mvt', 'att', 'mea', 'hist', 'note'];
        const slidesToRender = orderedSlideKeys.filter(k => activeSpecs.includes(k));

        return (
            <>
                <footer className="portrait-bottom-bar glass-panel">
                    <div className="portrait-bottom-left">
                        <button
                            className={`portrait-bottom-btn ${tablesOpen ? 'active' : ''}`}
                            onClick={() => setTablesOpen(v => !v)}
                            aria-label="Mesures"
                        >
                            <TablesIcon />
                        </button>
                    </div>

                    {!tablesOpen && <CompactPlayer />}

                    <div className="portrait-bottom-right">
                        {shouldShowLiveButtons && (
                            <button
                                className={`portrait-bottom-btn ${layoutState.live?.open ? 'active' : ''}`}
                                onClick={() => {
                                    dispatch({ type: 'FOOTER_VISIBLE', payload: false })
                                    dispatch({ type: 'LIVE_TOGGLE', payload: null })
                                }}
                                aria-label="Chat"
                            >
                                <ChatIcon />
                            </button>
                        )}
                        {shouldShowBottomLogo && (
                            <img src={process.env.REACT_APP_U1_VIEWERSVC_APP + "img/logo-C4W-light.svg"} alt="C4W" />
                        )}
                    </div>
                </footer>

                {tablesOpen && (
                    <div className="portrait-tables-panel glass-panel">
                        <div className="portrait-tables-header">
                            <span className="portrait-tables-patient">
                                {isConnected ? patientName : getAnonymous(patientName)}
                            </span>
                            <button className="portrait-tables-close" onClick={() => setTablesOpen(false)}>
                                <CloseIcon />
                            </button>
                        </div>
                        <div className="portrait-tables-swipe" onScroll={handleTablesScroll}>
                            {slidesToRender.map((key) => (
                                <div key={key} className="portrait-tables-slide">
                                    {key === 'mvt' && <MobileMovementsSection />}
                                    {key === 'att' && <MobileIprAttSection />}
                                    {key === 'mea' && <MobileMeasuresSection />}
                                    {(key === 'hist' || key === 'note') && (
                                        <Specification
                                            section={key}
                                            content={metas.treatment}
                                            specsActive={activeSpecs}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        {slidesToRender.length > 1 && (
                            <div className="portrait-tables-dots">
                                {slidesToRender.map((_, i) => (
                                    <span key={i} className={`dot ${i === currentSlideIndex ? 'active' : ''}`} />
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {isModelSelectorVisible && <ModelSelector />}
            </>
        );
    }

    /* ================================================================
     * Landscape / desktop layout (existing)
     * ================================================================ */
    const renderSimpleTabs = () => (
        <section id='specTabs'>
            <button
                key={'mod'}
                onClick={() => openSection('mod')}
                className={isModelSelectorVisible ? 'selected pointer' : 'black pointer'}
            >{ihm_viewer__dictionary['SPE_MODEL'][locale()]}</button>
            <CompactPlayer />
        </section>
    );

    const renderSpecTab = (section, label, isSelected = false) => (
        <button
            key={section}
            onClick={() => openSection(section)}
            className={`${isSelected ? 'selected pointer' : 'black pointer'} glass-panel`}
        >{label}</button>
    );

    const renderLiveButton = () => (
        <>
            {shouldShowLiveButtons && (
                <button
                    id="live"
                    className={`${layoutState.live?.open ? 'selected pointer' : 'black pointer'} glass-panel`}
                    onClick={() => {
                        dispatch({ type: 'FOOTER_VISIBLE', payload: false })
                        dispatch({ type: 'LIVE_TOGGLE', payload: null })
                    }}
                >
                    {ihm_viewer__dictionary['CHAT'][locale()]}
                </button>
            )}
        </>
    );

    // If there are no active specs, still render for 'simple' viewer (it must always show the model tab)
    if (activeSpecsEntries.length === 0 && viewerType !== 'simple') return null;

    const renderTabs = () => {
            if (viewerType === 'simple') {
                return renderSimpleTabs();
            }
        return (
            <section id='specTabs'>
                {!dataTableAccessDenied && activeSpecsEntries.length > 0
                    ? activeSpecsEntries.map(([section, value]) => {
                        const isSelected =
                            section === 'mod' ? isModelSelectorVisible
                                : footerVisibility === section && footerVisibility;

                        return renderSpecTab(section, ihm_viewer__dictionary[value.TRANS][locale()], isSelected);
                    })
                    : null}

                {renderLiveButton()}
                <CompactPlayer />
            </section>
        );
    };

    const renderEditModeTabs = () => {
        if (viewerType === 'simple') return renderSimpleTabs();

        return (
            <section id='specTabs'>
                {renderLiveButton()}
                <CompactPlayer />
            </section>
        );
    };
    const renderFooterContainer = () => {
        if (!footerVisibility || footerVisibility === 'mod') return null;

        return (
            <div id="footerContainer" className="glass-panel">
                <Specification section={footerVisibility} content={metas.treatment} specsActive={activeSpecNames} />
            </div>
        );
    };

    return (
        <>
            <footer>
                {isEditMode ? renderEditModeTabs() : (
                    <>
                        {renderTabs()}
                        {renderFooterContainer()}
                    </>
                )}
            </footer>

            {isModelSelectorVisible && <ModelSelector />}
        </>
    );
}
